import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const primaryNav = [
  { label: "Start", to: "/", testId: "nav-home" },
  { label: "Über uns", to: "/ueber-uns", testId: "nav-about" },
  { label: "Investmentfokus", to: "/investmentfokus", testId: "nav-fields" },
  { label: "Ablauf", to: "/ablauf", testId: "nav-process" },
  { label: "Insights", to: "/insights", testId: "nav-insights" },
  { label: "Kontakt", to: "/kontakt", testId: "nav-contact" },
];

const moreItems = [
  { label: "Werte", to: "/werte" },
  { label: "FAQ", to: "/faq" },
  { label: "Presse", to: "/presse" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/85 backdrop-blur-xl border-b border-white/5"
          : "bg-black/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3">
          <span className="block w-1.5 h-1.5 bg-[#D4AF37]" />
          <span className="font-display text-lg tracking-tight text-white">
            Wagenhofer <span className="text-zinc-400">Capital</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
          {primaryNav.map((item) => {
            const active = isActive(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={`relative text-[13px] tracking-wide transition-colors duration-300 ${
                  active ? "text-white" : "text-zinc-400 hover:text-white"
                }`}
              >
                {item.label}
                {active && (
                  <span className="absolute -bottom-2 left-0 right-0 h-px bg-[#D4AF37]" />
                )}
              </Link>
            );
          })}
          <div
            className="relative"
            onMouseEnter={() => setMoreOpen(true)}
            onMouseLeave={() => setMoreOpen(false)}
          >
            <button
              type="button"
              data-testid="nav-more-toggle"
              onClick={() => setMoreOpen((v) => !v)}
              className="flex items-center gap-1.5 text-[13px] tracking-wide text-zinc-400 hover:text-white transition-colors duration-300"
            >
              Mehr
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${
                  moreOpen ? "rotate-180" : ""
                }`}
                strokeWidth={1.5}
              />
            </button>
            {moreOpen && (
              <div
                data-testid="nav-more-menu"
                className="absolute right-0 top-full pt-3 min-w-[220px]"
              >
                <div className="bg-black/95 backdrop-blur-xl border border-white/10 py-3">
                  {moreItems.map((m) => (
                    <Link
                      key={m.to}
                      to={m.to}
                      className={`block px-5 py-2.5 text-[13px] tracking-wide transition-colors ${
                        isActive(m.to)
                          ? "text-[#D4AF37]"
                          : "text-zinc-300 hover:text-white hover:bg-white/5"
                      }`}
                    >
                      {m.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        <Link
          to="/kontakt"
          data-testid="nav-cta-contact"
          className="hidden lg:inline-block text-[11px] tracking-[0.28em] uppercase border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-300 px-5 py-3"
        >
          Gespräch
        </Link>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-white p-2"
          aria-label="Menü öffnen"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/5 max-h-[80vh] overflow-y-auto"
        >
          <div className="container-x py-6 flex flex-col gap-4">
            {primaryNav.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`${item.testId}-mobile`}
                className={`text-sm tracking-wide ${
                  isActive(item.to) ? "text-white" : "text-zinc-300"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="pt-4 mt-2 border-t border-white/10 flex flex-col gap-3">
              <span className="overline text-zinc-500">Mehr</span>
              {moreItems.map((m) => (
                <Link
                  key={m.to}
                  to={m.to}
                  className="text-sm tracking-wide text-zinc-400"
                >
                  {m.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
